import React from 'react';

import { Box } from '@mui/material';

import { UserProject } from 'state/userAdminSlices';

import { SpecialDateRequest, SpecialEventStatusVariant } from 'appTypes';
import dayjs from 'dayjs';
import StlTooltip from 'features/common/StlTooltip';
import {
  displayComparisonMonth,
  isFutureDate,
} from 'features/common/datetimeUtils';
import { MRT_Cell, MRT_ColumnDef } from 'material-react-table';

import { formatDateShort } from '../common';
import { StatusChip } from './StatusChip';

export const formatSpecialDates = (row: SpecialDateRequest) => {
  const { dates, date_range_start, date_range_end } = row;
  if (!dates || dates.length === 0) return '';
  const result: string[] = [];
  let rangeStartIndex = -1;
  let rangeEndIndex = -1;
  let hasRange = Boolean(date_range_start && date_range_end);
  if (hasRange) {
    rangeStartIndex = dates.indexOf(date_range_start);
    rangeEndIndex = dates.indexOf(date_range_end);
  }
  let i = 0;
  while (i < dates.length) {
    // If we are at the start of the range, output a single range block
    if (hasRange && i === rangeStartIndex && rangeEndIndex >= rangeStartIndex) {
      const start = formatDateShort(dates[rangeStartIndex]);
      const end = formatDateShort(dates[rangeEndIndex]);
      result.push(`${start} - ${end}`);
      i = rangeEndIndex + 1;
      hasRange = false;
    } else {
      result.push(formatDateShort(dates[i]));
      i++;
    }
  }
  return result.join(', ');
};

const tooltipTxt = 'Event data will be available after the scheduled date.';

export const columns: MRT_ColumnDef<any>[] = [
  {
    accessorKey: 'name',
    header: 'Special Event Name',
    Cell: ({ cell, row }) => (
      <StlTooltip
        title={isFutureDate(row.original.dates[0]) ? tooltipTxt : undefined}
        placement="top"
      >
        <span>{cell.getValue<string>()}</span>
      </StlTooltip>
    ),
    size: 176,
  },
  {
    accessorFn: (row) => formatSpecialDates(row),
    header: 'Date(s) of Event',
    size: 200,
  },
  {
    header: 'Comparison Month',
    size: 144,
    accessorFn: (row) => displayComparisonMonth(row.comparison_month),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    Cell: ({ cell }: { cell: MRT_Cell }) => (
      <Box>
        <StatusChip variant={cell.getValue<SpecialEventStatusVariant>()} />
      </Box>
    ),
    size: 96,
    grow: false,
  },
] as MRT_ColumnDef<any>[];

export const formatDateToString = (date: dayjs.Dayjs) => {
  return dayjs(date).format('YYYY-MM-DD');
};

export const formatDateToMonthYear = (date) => {
  return dayjs(date).format('YYYY-MM');
};

export function generateDateStringsFromRange(
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
): string[] {
  const dates: string[] = [];
  let current = start;
  const end_day = end;
  while (current <= end_day) {
    dates.push(formatDateToString(current));
    current = current.add(1, 'day');
  }

  return dates;
}

export const getMinDate = (): dayjs.Dayjs => {
  const d = dayjs().subtract(24, 'month');
  return d;
};

const INACTIVE = 'INACTIVE' as const;

export const isProjectActive = (project_data: UserProject): boolean => {
  if (!project_data) return false;
  const { status, project_end } = project_data;
  // project_end - (can be unset) provided as a status alone can fail
  if (project_end && dayjs(project_end) < dayjs()) return false;
  return status && status !== INACTIVE;
};
